if (!window.productPromoCopyListenerReady) {
  window.productPromoCopyListenerReady = true;

  const fallbackCopy = (value) => {
    const focusedElement = document.activeElement;
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);

    try {
      textarea.select();
      return document.execCommand('copy');
    } catch (error) {
      return false;
    } finally {
      textarea.remove();
      if (focusedElement instanceof HTMLElement) focusedElement.focus();
    }
  };

  const copyPromoCode = async (value) => {
    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function' &&
      window.isSecureContext
    ) {
      try {
        await navigator.clipboard.writeText(value);
        return true;
      } catch (error) {
        return fallbackCopy(value);
      }
    }

    return fallbackCopy(value);
  };

  const promoPayload = (notice) => ({
    promotion_id: notice?.dataset.promoId || notice?.dataset.promoCode || 'product-promo',
    promotion_name: notice?.dataset.promoName || notice?.textContent?.trim() || '',
    promo_code: notice?.dataset.promoCode || '',
    product_id: notice?.dataset.promoProductId || '',
    product_title: notice?.dataset.promoProductTitle || '',
    page_type: notice?.dataset.promoPageType || '',
    page_url: window.location.href,
  });

  const publishPromoEvent = (eventName, payload, retryCount = 0) => {
    if (
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      Promise.resolve(window.Shopify.analytics.publish(eventName, payload)).catch(() => {});
      return;
    }

    if (retryCount < 20) {
      window.setTimeout(() => publishPromoEvent(eventName, payload, retryCount + 1), 250);
    }
  };

  const promoGa4CurrentId = 'G-489FD2QNZN';
  const promoGa4DestinationIds = [promoGa4CurrentId, 'G-3G3MQF5QTX'];
  const pendingGa4Events = [];
  let promoGa4RetryCount = 0;
  let promoGa4RetryTimer;
  let promoGa4CurrentConfigured = false;

  const flushPendingGa4Events = () => {
    if (!pendingGa4Events.length) return;

    if (!window.analyzify?.ga4Initiated || !Array.isArray(window.dataLayer)) {
      if (promoGa4RetryCount < 240 && !promoGa4RetryTimer) {
        promoGa4RetryCount += 1;
        promoGa4RetryTimer = window.setTimeout(() => {
          promoGa4RetryTimer = undefined;
          flushPendingGa4Events();
        }, 500);
      }
      return;
    }

    const promoGtag = function () {
      window.dataLayer.push(arguments);
    };

    if (!promoGa4CurrentConfigured) {
      promoGtag('config', promoGa4CurrentId, { send_page_view: false });
      promoGa4CurrentConfigured = true;
    }

    while (pendingGa4Events.length) {
      const { eventName, eventPayload } = pendingGa4Events.shift();
      promoGtag('event', eventName, eventPayload);
    }

    promoGa4RetryCount = 0;
  };

  const sendGa4Event = (eventName, payload) => {
    const creativeSlot = `${payload.page_type || 'unknown'}_promo_notice`;
    const item = {
      item_id: payload.product_id || undefined,
      item_name: payload.product_title || undefined,
      promotion_id: payload.promotion_id,
      promotion_name: payload.promotion_name,
      creative_slot: creativeSlot,
      coupon: payload.promo_code || undefined,
    };

    promoGa4RetryCount = 0;
    pendingGa4Events.push({
      eventName,
      eventPayload: {
        send_to: promoGa4DestinationIds,
        event_category: 'promotion',
        promotion_id: payload.promotion_id,
        promotion_name: payload.promotion_name,
        creative_slot: creativeSlot,
        coupon: payload.promo_code || undefined,
        promo_code: payload.promo_code,
        product_id: payload.product_id,
        product_name: payload.product_title,
        page_type: payload.page_type,
        interaction_type: payload.interaction_type || undefined,
        page_location: payload.page_url || window.location.href,
        debug_mode: new URLSearchParams(window.location.search).has('pv_promo_debug') || undefined,
        items: [item],
      },
    });

    flushPendingGa4Events();
  };

  document.addEventListener('visitorConsentCollected', flushPendingGa4Events);
  document.addEventListener('AnalyzifyConsent', flushPendingGa4Events);

  window.pvPromoAnalytics = {
    send: sendGa4Event,
    flush: flushPendingGa4Events,
  };

  const trackPromoEvent = (shopifyEventName, ga4EventName, notice, extra = {}) => {
    const payload = { ...promoPayload(notice), ...extra };
    publishPromoEvent(shopifyEventName, payload);
    sendGa4Event(ga4EventName, payload);
  };

  const trackPromoCodeCopy = (button) => {
    const notice = button.closest('.product-price__promo-notice');
    trackPromoEvent('pv:promo_code_copy', 'select_promotion', notice, {
      promo_code: button.dataset.promoCopyCode || '',
      interaction_type: 'copy_code',
    });
  };

  const observedPromoNotices = new WeakSet();
  const viewedPromoKeys = new Set();
  const promoObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;

        const notice = entry.target;
        const payload = promoPayload(notice);
        const viewKey = `${payload.promotion_id}|${payload.product_id}|${payload.page_type}`;
        promoObserver.unobserve(notice);
        if (viewedPromoKeys.has(viewKey)) return;

        viewedPromoKeys.add(viewKey);
        trackPromoEvent('pv:promo_view', 'view_promotion', notice);
      });
    },
    { threshold: 0.5 }
  );

  const observePromoNotices = () => {
    document.querySelectorAll('.product-price__promo-notice').forEach((notice) => {
      if (observedPromoNotices.has(notice)) return;
      observedPromoNotices.add(notice);
      promoObserver.observe(notice);
    });
  };

  const startPromoNoticeTracking = () => {
    observePromoNotices();
    const promoNoticeObserver = new MutationObserver(observePromoNotices);
    promoNoticeObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) {
    startPromoNoticeTracking();
  } else {
    document.addEventListener('DOMContentLoaded', startPromoNoticeTracking, { once: true });
  }

  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.product-price__promo-code[data-promo-copy-code]');
    if (!button) {
      const addButton = event.target.closest(
        '.product-form-buttons button[type="submit"], button[name="add"], [data-add-to-cart]'
      );
      if (!addButton) return;

      const notice = document.querySelector(
        '.product-price__promo-notice[data-promo-page-type="product"]'
      );
      if (notice) {
        trackPromoEvent('pv:promo_add_to_cart', 'promo_add_to_cart', notice, {
          interaction_type: 'add_to_cart',
        });
      }
      return;
    }

    const copied = await copyPromoCode(button.dataset.promoCopyCode);
    const status = button.closest('.product-price__promo-notice')?.querySelector('[data-promo-copy-status]');

    if (status) {
      status.textContent = copied
        ? `Code ${button.dataset.promoCopyCode} copied`
        : 'Could not copy code';
    }

    if (!copied) return;

    trackPromoCodeCopy(button);
    button.classList.add('is-copied');
    button.setAttribute('aria-label', `Code ${button.dataset.promoCopyCode} copied`);
    window.clearTimeout(button.productPromoCopyTimer);
    button.productPromoCopyTimer = window.setTimeout(() => {
      button.classList.remove('is-copied');
      button.setAttribute('aria-label', `Copy code ${button.dataset.promoCopyCode}`);
      if (status) status.textContent = '';
    }, 1600);
  });
}

if (!window.productPromoPlacementReady) {
  window.productPromoPlacementReady = true;

  const positionProductPromoNotice = () => {
    const notice = document.querySelector(
      '.product-price__promo-notice[data-promo-page-type="product"]'
    );
    const purchaseButtons = document.querySelector(
      '.buy-buttons-block .product-form-buttons'
    );

    if (!notice || !purchaseButtons || purchaseButtons.nextElementSibling === notice) return;

    notice.classList.add('product-price__promo-notice--after-buttons');
    purchaseButtons.insertAdjacentElement('afterend', notice);
  };

  positionProductPromoNotice();

  const promoPlacementObserver = new MutationObserver(() => {
    window.requestAnimationFrame(positionProductPromoNotice);
  });

  promoPlacementObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
